import { supabase } from '../lib/supabase';

export const alertManagementService = {
  // Get all incidents with optional filters
  async getIncidents(filters = {}) {
    try {
      let query = supabase?.from('service_incidents')?.select(`
          *,
          acknowledged_by_user:user_profiles!service_incidents_acknowledged_by_fkey(id, email, full_name),
          resolved_by_user:user_profiles!service_incidents_resolved_by_fkey(id, email, full_name)
        `)?.order('detected_at', { ascending: false });

      if (filters?.severity) {
        query = query?.eq('severity', filters?.severity);
      }

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.serviceName) {
        query = query?.eq('service_name', filters?.serviceName);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching incidents:', error);
      throw error;
    }
  },

  // Get incident by ID
  async getIncidentById(incidentId) {
    try {
      const { data, error } = await supabase?.from('service_incidents')?.select(`
          *,
          acknowledged_by_user:user_profiles!service_incidents_acknowledged_by_fkey(id, email, full_name),
          resolved_by_user:user_profiles!service_incidents_resolved_by_fkey(id, email, full_name),
          acknowledgments:incident_acknowledgments(
            *,
            user:user_profiles(id, email, full_name)
          )
        `)?.eq('id', incidentId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching incident:', error);
      throw error;
    }
  },

  // Acknowledge incident
  async acknowledgeIncident(incidentId, userId, notes = '') {
    try {
      // Update incident status
      const { error: updateError } = await supabase?.from('service_incidents')?.update({
          status: 'acknowledged',
          acknowledged_at: new Date()?.toISOString(),
          acknowledged_by: userId
        })?.eq('id', incidentId);

      if (updateError) throw updateError;

      // Create acknowledgment record
      const { data, error: ackError } = await supabase?.from('incident_acknowledgments')?.insert({
          incident_id: incidentId,
          user_id: userId,
          notes
        })?.select()?.single();

      if (ackError) throw ackError;
      return data;
    } catch (error) {
      console.error('Error acknowledging incident:', error);
      throw error;
    }
  },

  // Bulk acknowledge incidents
  async bulkAcknowledgeIncidents(incidentIds, userId) {
    try {
      const timestamp = new Date()?.toISOString();

      // Update all incidents
      const { error: updateError } = await supabase?.from('service_incidents')?.update({
          status: 'acknowledged',
          acknowledged_at: timestamp,
          acknowledged_by: userId
        })?.in('id', incidentIds);

      if (updateError) throw updateError;

      // Create acknowledgment records
      const acknowledgments = incidentIds?.map(id => ({
        incident_id: id,
        user_id: userId,
        notes: 'Bulk acknowledgment'
      }));

      const { error: ackError } = await supabase?.from('incident_acknowledgments')?.insert(acknowledgments);

      if (ackError) throw ackError;
      return { success: true, count: incidentIds?.length };
    } catch (error) {
      console.error('Error bulk acknowledging incidents:', error);
      throw error;
    }
  },

  // Resolve incident
  async resolveIncident(incidentId, userId, resolutionNotes) {
    try {
      const { data, error } = await supabase?.from('service_incidents')?.update({
          status: 'resolved',
          resolved_at: new Date()?.toISOString(),
          resolved_by: userId,
          resolution_notes: resolutionNotes
        })?.eq('id', incidentId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error resolving incident:', error);
      throw error;
    }
  },

  // Get incident statistics
  async getIncidentStats() {
    try {
      const { data, error } = await supabase?.from('service_incidents')?.select('severity, status');

      if (error) throw error;

      const stats = {
        total: data?.length || 0,
        bySeverity: {
          critical: 0,
          high: 0,
          medium: 0,
          low: 0
        },
        byStatus: {
          open: 0,
          acknowledged: 0,
          resolved: 0,
          closed: 0
        }
      };

      data?.forEach(incident => {
        if (incident?.severity) stats.bySeverity[incident.severity]++;
        if (incident?.status) stats.byStatus[incident.status]++;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching incident stats:', error);
      throw error;
    }
  },

  // Get escalation rules
  async getEscalationRules(userId) {
    try {
      const { data, error } = await supabase?.from('escalation_rules')?.select('*')?.eq('user_id', userId)?.order('severity_level', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching escalation rules:', error);
      throw error;
    }
  },

  // Create escalation rule
  async createEscalationRule(ruleData) {
    try {
      const { data, error } = await supabase?.from('escalation_rules')?.insert(ruleData)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating escalation rule:', error);
      throw error;
    }
  },

  // Update escalation rule
  async updateEscalationRule(ruleId, updates) {
    try {
      const { data, error } = await supabase?.from('escalation_rules')?.update(updates)?.eq('id', ruleId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating escalation rule:', error);
      throw error;
    }
  },

  // Delete escalation rule
  async deleteEscalationRule(ruleId) {
    try {
      const { error } = await supabase?.from('escalation_rules')?.delete()?.eq('id', ruleId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting escalation rule:', error);
      throw error;
    }
  },

  // Trigger manual escalation
  async triggerEscalation(incidentId) {
    try {
      // First, get current escalation count
      const { data: currentData, error: fetchError } = await supabase?.from('service_incidents')?.select('escalation_count')?.eq('id', incidentId)?.single();
      
      if (fetchError) throw fetchError;
      
      const newEscalationCount = (currentData?.escalation_count || 0) + 1;
      
      const { data, error } = await supabase?.from('service_incidents')?.update({
          escalation_count: newEscalationCount,
          last_escalated_at: new Date()?.toISOString()
        })?.eq('id', incidentId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error triggering escalation:', error);
      throw error;
    }
  }
};